import { useEffect, useRef } from 'react';

interface UseTimelineProgressResult {
  listRef: React.RefObject<HTMLOListElement>;
  lineRef: React.RefObject<HTMLDivElement>;
}

const useTimelineProgress = (): UseTimelineProgressResult => {
  const listRef = useRef<HTMLOListElement>(null);
  const lineRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let frame: number | null = null;

    const updateProgress = () => {
      frame = null;
      const list = listRef.current;
      const line = lineRef.current;
      if (!list || !line) {
        return;
      }
      const { top, height } = list.getBoundingClientRect();
      const viewportMiddle = window.innerHeight / 2;

      const nodes = list.querySelectorAll<HTMLElement>('.ParagraphTimelineItem__node');
      const firstNode = nodes[0];
      const lastNode = nodes[nodes.length - 1];

      // Node middles are computed directly in viewport space (rect.top is
      // already viewport-relative), so progress needs no extra conversion
      // back and forth through the list's own top offset.
      const getNodeMiddle = (node: HTMLElement) => {
        const rect = node.getBoundingClientRect();
        return rect.top + (rect.height / 2);
      };
      const firstNodeMiddle = firstNode ? getNodeMiddle(firstNode) : top;
      const lastNodeMiddle = lastNode ? getNodeMiddle(lastNode) : top + height;

      const currentLineStart = firstNodeMiddle - top;
      const currentLineLength = lastNodeMiddle - firstNodeMiddle;

      // Progress must scale against the line's own length (from the first
      // node to the last), not the full list height, or the fill and the
      // node color toggles below fall out of sync.
      const value = currentLineLength > 0
        ? (viewportMiddle - firstNodeMiddle) / currentLineLength
        : 0;

      const clampedProgress = Math.min(1, Math.max(0, value));

      // Write CSS custom properties directly on the DOM element, bypassing
      // React state and its re-render cycle. This keeps the animation in
      // sync with the browser's own paint tick (rAF) and avoids the
      // scroll → setState → reconcile → commit lag that causes jank in Chrome.
      line.style.setProperty('--progress', String(clampedProgress));
      line.style.setProperty('--line-start', `${currentLineStart}px`);
      line.style.setProperty('--line-length', `${currentLineLength}px`);

      // Position each node so its center aligns with the bar's center.
      // Bar center = line.left + 2px (bar is left:0 on __line, width 4px).
      // Percentage-based CSS left can't be used reliably here because the node's
      // containing block (__content) may differ in width from __row at runtime.
      const lineLeft = line.getBoundingClientRect().left;
      const barCenterX = lineLeft + 2; // half of 0.4rem bar width

      nodes.forEach((node) => {
        const content = node.parentElement;
        if (content) {
          const contentLeft = content.getBoundingClientRect().left;
          // node width = 1.4rem = 14px → half = 7px
          node.style.setProperty('left', `${barCenterX - contentLeft - 7}px`);
        }

        const nodeRect = node.getBoundingClientRect();
        const nodeMiddle = nodeRect.top + (nodeRect.height / 2);
        node.classList.toggle('ParagraphTimelineItem__node--passed', nodeMiddle <= viewportMiddle);
      });
    };

    const handleScroll = () => {
      if (frame === null) {
        frame = window.requestAnimationFrame(updateProgress);
      }
    };

    updateProgress();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);

    return () => {
      if (frame !== null) {
        window.cancelAnimationFrame(frame);
      }
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  return { listRef, lineRef };
};

export default useTimelineProgress;
